import React from 'react'
import PropTypes from 'prop-types'
import useCompanyItemStore from '@store/useCompanyItemStore'
import companyItemService from '@services/companyItemService'
import { Modal, Button } from '@components/common'
import { Download, RefreshCw, FileText, Calendar, AlertCircle } from 'lucide-react'

/**
 * ResourceView Component
 *
 * Modal for viewing resource details with download and update options.
 */
const ResourceView = ({ onClose }) => {
  const { selectedItem, downloadItem, updateToLatestVersion, loading } = useCompanyItemStore()

  if (!selectedItem) return null

  const getTypeColor = (type) => {
    const colors = {
      policy: '#3b82f6',
      procedure: '#10b981',
      template: '#8b5cf6',
      guideline: '#f59e0b',
      resource: '#6366f1',
    }
    return colors[type] || '#6b7280'
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleDownload = () => {
    downloadItem(selectedItem.item_id)
  }

  const handleUpdate = async () => {
    await updateToLatestVersion(selectedItem.item_id)
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Resource Details"
      size="medium"
      closeOnBackdrop={false}
    >
      <div className="resource-view">
        {/* Header */}
        <div className="resource-view__header">
          <div
            className="resource-view__icon"
            style={{
              backgroundColor: `${getTypeColor(selectedItem.item_type)}20`,
              color: getTypeColor(selectedItem.item_type),
            }}
          >
            <FileText size={28} />
          </div>
          <div className="resource-view__title-section">
            <h2 className="resource-view__title">{selectedItem.title}</h2>
            <div className="resource-view__badges">
              <span
                className="type-badge"
                style={{
                  backgroundColor: `${getTypeColor(selectedItem.item_type)}20`,
                  color: getTypeColor(selectedItem.item_type),
                }}
              >
                {selectedItem.item_type.charAt(0).toUpperCase() + selectedItem.item_type.slice(1)}
              </span>
              <span className="version-badge">v{selectedItem.item_version}</span>
            </div>
          </div>
        </div>

        {/* Update Available Banner */}
        {selectedItem.has_update && (
          <div className="resource-view__update-banner">
            <AlertCircle size={20} />
            <div className="resource-view__update-text">
              <strong>Update Available!</strong> A newer version (v{selectedItem.current_version}) is available.
            </div>
            <Button size="small" variant="warning" onClick={handleUpdate} disabled={loading}>
              <RefreshCw size={14} />
              Update Now
            </Button>
          </div>
        )}

        {/* Description */}
        {selectedItem.description && (
          <div className="detail-section">
            <h3 className="detail-section__title">Description</h3>
            <p className="resource-view__description">{selectedItem.description}</p>
          </div>
        )}

        {/* File Information */}
        <div className="detail-section">
          <h3 className="detail-section__title">File Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-item__label">File Name</span>
              <span className="detail-item__value">{selectedItem.original_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item__label">File Size</span>
              <span className="detail-item__value">
                {companyItemService.formatFileSize(selectedItem.file_size)}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-item__label">Your Version</span>
              <span className="detail-item__value">v{selectedItem.item_version}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item__label">Latest Version</span>
              <span className="detail-item__value">v{selectedItem.current_version}</span>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="detail-section">
          <h3 className="detail-section__title">Timeline</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-item__label">
                <Calendar size={14} /> Last Updated
              </span>
              <span className="detail-item__value">{formatDate(selectedItem.item_updated_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item__label">
                <Calendar size={14} /> Assigned On
              </span>
              <span className="detail-item__value">{formatDate(selectedItem.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleDownload}>
            <Download size={16} />
            Download
          </Button>
        </div>
      </div>
    </Modal>
  )
}

ResourceView.propTypes = {
  onClose: PropTypes.func.isRequired,
}

export default ResourceView
